import classNames from "classnames";
import { useFormik } from "formik";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import ModalComponent from "..";
import { CanadianAlcoholNotice } from "../../event/canadianAlcoholNotice";
import {
  CreateTicket,
  TicketQuantityType,
  TicketType,
} from "../../../redux/features/event/event.types";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import {
  currencyFull,
  currencyOptions,
  currencyShorts,
  PRODUCT_PRICING,
} from "../../../utils/constants";
import {
  deepCopy,
  formatNumber,
  getCurrencySymbol,
  parseStringToNumber,
} from "../../../utils/helpers";
import { ModalCompProps } from "../../../utils/types";
import { EventTicketSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import ComboBox from "../../dropdown/comboBox";
import Dropdown from "../../dropdown/dropdown";
import { CaretDown } from "../../icon/downcaret";
import { SwitchInput } from "../../inputs/switch";
import { TextInput } from "../../inputs/textInput";
import { TabNav } from "../../tabNav";
import { TabSelect } from "../../tabNav/tabSelect";

const ticketNameSuggestions = ["Regular", "Early Bird", "VIP", "VVIP"];

export const EventTicketModal = ({
  open,
  handleClose,
  ticket,
  selectedIdx,
  tickets,
  onSuccess = () => {},
  returnMode = "array",
  loading = false,
  quantityPurchased,
  lockCurrency,
  isPublishedEventTicket = false,
}: ModalCompProps & {
  ticket?: CreateTicket & { _id?: string; quantity?: number };
  selectedIdx?: number;
  tickets: CreateTicket[];
  onSuccess: (
    tickets: CreateTicket[] | CreateTicket,
    reset: () => void,
  ) => void;
  returnMode?: "array" | "singleValue";
  loading?: boolean;
  quantityPurchased?: number;
  lockCurrency?: PayoutCurrency;
  isPublishedEventTicket?: boolean;
}) => {
  const {
    handleSubmit,
    values,
    touched,
    handleBlur,
    handleChange,
    errors,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      type: ticket?.ticketType ?? "paid",
      name: ticket?.name ?? "",
      description: ticket?.description ?? "",
      price: String(ticket?.price ?? ""),
      quantityType: ticket?.quantityType ?? "limited",
      quantity: String(ticket?.quantity ?? "0"),
      currency: !!lockCurrency ? lockCurrency : (ticket?.currency ?? "NGN"),
      soldOut: ticket
        ? ticket.quantityType === "limited" &&
          (ticket as any).quantityAvailable <= (quantityPurchased ?? 0)
        : false,
    },
    enableReinitialize: true,
    validationSchema: EventTicketSchema(quantityPurchased ?? 0),
    onSubmit: async (vals) => {
      const ticketQuantity =
        vals.quantityType === "unlimited"
          ? 0
          : (parseStringToNumber(String(vals.quantity)) ?? 0);

      const newTicket: CreateTicket = {
        name: vals.name,
        ticketType: vals.type as TicketType,
        price: parseStringToNumber(String(values.price)) ?? 0,
        description: vals.description !== "" ? vals.description : undefined,
        quantityType: vals.quantityType as TicketQuantityType,
        quantityAvailable: vals.soldOut ? (quantityPurchased ?? 0) : ticketQuantity,
        quantity: ticketQuantity,
        currency: vals.currency,
      };

      if (returnMode === "array") {
        const ticketsCopy = deepCopy(tickets);

        if (ticket && selectedIdx !== undefined) {
          // Replace with edited values
          ticketsCopy[selectedIdx!] = newTicket;

          //Update all paid ticket currencies to use same one
          onSuccess(
            ticketsCopy.map((t) => ({
              ...t,
              currency: t.ticketType === "paid" ? vals.currency : undefined,
            })),
            () => {
              resetForm();
            },
          );

          close();
        } else {
          ticketsCopy.push(newTicket);
          toast.success("Ticket Created");

          //Update all paid ticket currencies to use same one
          onSuccess(
            ticketsCopy.map((t) => ({
              ...t,
              currency: t.ticketType === "paid" ? vals.currency : undefined,
            })),
            () => {
              resetForm();
            },
          );

          close();
        }
      } else {
        onSuccess(newTicket, () => {
          resetForm();
        });
      }
    },
  });

  const close = () => {
    resetForm();
    handleClose();
  };

  return (
    <ModalComponent
      open={open}
      handleClose={close}
      className="!p-"
      dialogClassName=""
      closeOnOverlayClick
      showHeader
      title={ticket ? "Edit Ticket" : "Create Ticket"}
      subtitle={
        ticket
          ? "These changes would only apply to new tickets that are being sold. Tickets that have already been purchased won’t be affected."
          : `We don't charge fees for free tickets. For paid tickets, we charge a ${PRODUCT_PRICING[values.currency].feeText} per ticket and this fee is passed to the attendees by default.`
      }
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form className="grid grid-cols-2 items-start gap-4">
        {isPublishedEventTicket ? undefined : (
          <TabNav
            navs={[
              {
                title: "Paid",
                to: "paid",
                onClick: (selected) => {
                  setFieldValue("price", "0");
                  setFieldValue("type", selected);
                },
              },
              {
                title: "Free",
                to: "free",
                onClick: (selected) => {
                  setFieldValue("price", "0");
                  setFieldValue("type", selected);
                },
              },
            ]}
            activeTab={values.type}
            variant="switcher"
            tabClassName=""
            className="col-span-full"
          />
        )}

        <div className="group col-span-full flex flex-col gap-4">
          <TextInput
            type="text"
            name="name"
            value={values.name}
            placeholder="Enter ticket name"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Ticket Name"
            errors={errors.name && touched.name ? errors.name : ""}
            containerClassName=""
          />

          <div
            className={classNames("hidden animate-fade-in flex-col gap-1", {
              "flex group-focus-within:flex": ["", undefined].includes(
                values.name.replaceAll(" ", ""),
              ),
            })}
          >
            <p className="text-sm font-bold">Suggestions:</p>

            <TabSelect
              tabs={ticketNameSuggestions.map((name) => ({
                title: name,
                to: name,
                onClick: () => {
                  setFieldValue("name", name);
                },
              }))}
            />
          </div>
        </div>

        <TextInput
          type="textarea"
          name="description"
          value={values.description}
          placeholder="Enter description"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Description (Optional)"
          errors={
            errors.description && touched.description ? errors.description : ""
          }
          containerClassName="col-span-full"
          rows={1}
        />

        {values.type === "paid" && (
          <>
            {isPublishedEventTicket ? undefined : (
              <ComboBox
                menuItems={currencyOptions}
                onSelectOption={(selected) => {
                  setFieldValue("currency", selected);
                }}
                menuClassName="!max-h-[200px] translate-x-[-12px] !z-[999]"
                position="bottom start"
                immediate
                disabled={!!lockCurrency}
                type="text"
                name="currency"
                value={
                  values.currency ? currencyFull[values.currency] : undefined
                }
                placeholder={"Select currency"}
                onChange={handleChange}
                onBlur={handleBlur}
                hasTitle
                title={"Currency"}
                errors={
                  errors.currency && touched.currency ? errors.currency : ""
                }
                readOnly
                containerClassName="col-span-full"
                customIcon={<CaretDown />}
                customIconPosition="right"
              />
            )}

            <NumericFormat
              suffix={undefined}
              prefix={getCurrencySymbol(
                currencyShorts[values.currency ?? "NGN"],
              )}
              decimalScale={2}
              thousandSeparator
              allowLeadingZeros={false}
              customInput={TextInput}
              type="text"
              name="price"
              value={values.price}
              placeholder="Enter ticket price"
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title="Price"
              errors={errors.price && touched.price ? errors.price : ""}
              containerClassName={isPublishedEventTicket ? "col-span-full" : ""}
              disabled={isPublishedEventTicket && !!ticket}
            />
          </>
        )}

        <Dropdown
          menuItems={[
            {
              name: "Unlimited",
              value: "Unlimited",
              onClick: () => {
                setFieldValue("quantity", Infinity);
                setFieldValue("quantityType", "unlimited");
              },
            },
            {
              name: "Limited",
              value: "Limited",
              onClick: () => {
                setFieldValue("quantity", "0");
                setFieldValue("quantityType", "limited");
              },
            },
          ]}
          position={"bottom start"}
          menuClassName="translate-x-[-12px] !z-[999]"
          menuItemsClassName=""
        >
          <TextInput
            type="text"
            name="quantityType"
            value={values.quantityType}
            placeholder="Select quantity type"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Quantity Type"
            readOnly
            customIcon={<CaretDown />}
            customIconPosition="right"
            errors={
              errors.quantityType && touched.quantityType
                ? errors.quantityType
                : ""
            }
            inputClass="capitalize"
          />
        </Dropdown>

        <TextInput
          type="text"
          name="quantity"
          value={formatNumber(
            values.quantityType === "unlimited"
              ? Infinity
              : (values.quantity ?? ""),
          )}
          placeholder="Enter ticket quantity"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Quantity"
          readOnly={values.quantityType === "unlimited"}
          errors={errors.quantity && touched.quantity ? errors.quantity : ""}
        />

        {isPublishedEventTicket && ticket && values.quantityType === "limited" && (
          <div className="col-span-full flex items-center justify-between gap-3 rounded-2xl border border-grey-200 p-3">
            <div>
              <p className="text-sm font-bold text-grey-900">Set Ticket as Sold Out</p>
              <p className="text-sm text-grey-600">
                New guests won't be able to buy this ticket
              </p>
            </div>
            <SwitchInput
              checked={values.soldOut}
              onChange={(checked) => setFieldValue("soldOut", !checked)}
            />
          </div>
        )}

        <CanadianAlcoholNotice
          currency={values.type === "paid" ? values.currency : lockCurrency}
        />

        <div className="col-span-full grid w-full grid-cols-2 items-center justify-end gap-2 md:flex">
          <Button
            text="Cancel"
            type="button"
            onClick={() => {
              close();
            }}
            variation="plain-grey"
            className="md:max-w-fit"
          />
          <Button
            text={ticket !== undefined ? "Save Changes" : "Create"}
            type="button"
            onClick={() => {
              handleSubmit();
            }}
            className="md:max-w-fit"
            loading={loading}
            disabled={loading}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
